import { ErrorHandler, Injectable } from '@angular/core';
import { StorageService } from 'shared/services';

/**
 * @description
 * Global error handler for handling chunk load errors in Angular applications (i.e. the .js file for a module hasn't loaded by the time it needs to be used).
 *
 * It listens all requests for errors during and only acts on the 'ChunkLoadError'. It then reloads the application only once (to avoid infinite reloads).
 * It also adds a timepstamp to the chunk load error to handle the case where the internet briefly stops working for a user and they try to click twice on the same component.
 * If the chunk fails to load again, it logs the error to the console for further troubleshooting.
 * It uses sessionStorage to keep track of the chunks that have already been retried to avoid infinite reloads.
 */

const RETRY_EXPIRY_TIME_MS = 60 * 1000; // 1 min
const CHUNK_RETRY_MAP_KEY = 'chunkRetryMap';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    constructor(private storageService: StorageService) {}

    handleError(error: any): void {
        if (this.isChunkLoadError(error)) {
            const file = this.extractChunkUrl(error);
            const retries = this.getRetryMap();
            const currentTime = Date.now();

            if (file) {
                const failedEntry = retries[file];
                if (!failedEntry || currentTime - failedEntry.timestamp > RETRY_EXPIRY_TIME_MS) {
                    console.warn(`[ChunkLoadError] Cannot load chunk ${file}. Will attempt app reload.`);
                    retries[file] = { retried: true, timestamp: currentTime };
                    this.saveRetryMap(retries);
                    window.location.reload();
                    return;
                } else if (file) {
                    console.error(`[ChunkLoadError] Cannot load chunk ${file} on app reload.`);
                    delete retries[file];
                    this.saveRetryMap(retries);
                }
            }
        }

        throw error;
    }

    private isChunkLoadError(error: any): boolean {
        return error?.stack?.includes('ChunkLoadError') || error?.message?.includes('Loading chunk');
    }

    private extractChunkUrl(error: any): string | null {
        const match = error?.message?.match(/\/([^\/]+\.js)/);
        return match?.[1] ?? null;
    }

    private getRetryMap(): Record<string, { retried: boolean; timestamp: number }> {
        try {
            const valueInStorage = this.storageService.getFromSession(CHUNK_RETRY_MAP_KEY);
            return JSON.parse((valueInStorage as string) || '{}');
        } catch {
            return {};
        }
    }

    private saveRetryMap(map: Record<string, { retried: boolean; timestamp: number }>): void {
        if (Object.keys(map).length === 0) {
            this.storageService.removeFromSession(CHUNK_RETRY_MAP_KEY);
        } else {
            this.storageService.setInSession(CHUNK_RETRY_MAP_KEY, JSON.stringify(map));
        }
    }
}
